import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '../../lib/react-query'
import { valueApiFor } from '../../services/api/assetValue.api'
import { useAuthStore } from '../../store/authStore'
import type { AssetValue, AssetValueInput, ValuationKind } from '../../app/modules/types/assetValue'

/**
 * Business value — the read trail and the two writes.
 *
 * **There is no `useAssetValue(id)`.** The valuation is already in the asset
 * detail payload under `value` (always present, `null` when undeclared), so
 * the card reads it from the existing detail hooks. Adding a hook that calls
 * `GET …/value/` would spend a second request to learn what the page already
 * knows, and would have to treat that endpoint's 404 — which means
 * "undeclared", not "failed" — as a success.
 *
 * Keyed by `kind` throughout so the domain and IP pages share one
 * implementation without sharing a cache entry.
 *
 * Imports `valueApiFor` DIRECTLY from `../../services/api/assetValue.api`,
 * never via the `services/api` barrel — the barrel loads `componentIcons.ts`,
 * whose module-scope `require.context` throws under Jest.
 */

/**
 * The `assets`/`ips` slice of the key registry for a resource kind.
 *
 * `ValuationKind` is `'assets' | 'ip'` (both the URL segment and, confusingly,
 * *almost* the cache scope). The `queryKeys.ts` slices are `assets` and
 * **`ips`** — plural. `'ip' → 'ips'` is the trap: get it backwards and a save
 * invalidates a key nobody reads, so the card silently never refreshes. Every
 * later reader of a cache scope for this subsystem goes through `keysFor`,
 * never re-derives the mapping inline.
 *
 * Exported so `useValueSuggestions` (Task 20) reuses this exact mapping
 * instead of re-deriving `'assets' | 'ips'` a second time — the same
 * singular/plural trap, one fewer place it can be gotten wrong.
 */
export function keysFor(kind: ValuationKind) {
  return kind === 'ip' ? queryKeys.ips : kind === 'files' ? queryKeys.files : queryKeys.assets
}

/** HTTP status off an axios-shaped rejection, without importing axios types. */
function statusOf(error: unknown): number | undefined {
  return (error as { response?: { status?: number } } | null)?.response?.status
}

/**
 * A file's valuation.
 *
 * Unlike a host or an address, a file's detail row carries only the
 * `criticality` tier, so the card's full valuation comes from
 * `GET /files/{id}/value/`. Its 404 means "undeclared", not "failed", and
 * resolves to `null` so the card renders its empty state. Lives under
 * `files.all`, so a write drops it with everything else.
 */
export function useFileValue(id: string | null) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.files.value(orgId, id ?? ''),
    queryFn: async (): Promise<AssetValue | null> => {
      try {
        return await valueApiFor('files').get(id as string)
      } catch (error) {
        if (statusOf(error) === 404) return null
        throw error
      }
    },
    enabled: !!id && !!orgId,
    retry: false,
  })
}

/**
 * One page of the change trail, fetched when the History dialog opens.
 *
 * `enabled` is the dialog's own open state: the trail is a click away from
 * the card, not part of it, so nothing is fetched for the overwhelming
 * majority of visits that never open it.
 */
export function useAssetValueHistory(
  kind: ValuationKind,
  id: string | null,
  page: number,
  enabled = true
) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: keysFor(kind).valueHistory(orgId, id || '', page),
    queryFn: () => valueApiFor(kind).history(id as string, page),
    enabled: !!id && !!orgId && enabled,
    /**
     * A trail page never changes once written — events are append-only and
     * the newest is first — so the previous page stays on screen while the
     * next one loads instead of the list blanking on every "Load more".
     */
    placeholderData: (previous) => previous,
  })
}

/**
 * Everything a write has to drop.
 *
 * All of list / detail / filterOptions / valueHistory, every time: the DETAIL
 * carries the `value` the card renders, the HISTORY gained a row, the LIST
 * row carries the tier a chip will show, and the facet counts shifted.
 * Invalidating only the detail leaves the grid reading "Not set" after a save.
 *
 * `keysFor(kind).all(orgId)` is the prefix of every one of those keys, so a
 * single invalidation covers all four.
 */
function useInvalidateValue(kind: ValuationKind) {
  const queryClient = useQueryClient()
  const orgId = useAuthStore((state) => state.orgId)
  const keys = keysFor(kind)
  return () => queryClient.invalidateQueries({ queryKey: keys.all(orgId) })
}

/**
 * Declare or amend.
 *
 * Resolves to `{ value, created }` — `created` is the `201` vs `200`
 * distinction, and the ONLY thing that tells "Value declared." from "Value
 * updated.". Read it from the mutation result, never inferred from whether
 * the card had a value before — that would be wrong the moment two tabs are
 * open.
 */
export function usePutAssetValue(kind: ValuationKind, id: string | null) {
  const invalidate = useInvalidateValue(kind)
  return useMutation({
    mutationFn: (body: AssetValueInput) => valueApiFor(kind).put(id as string, body),
    onSuccess: () => void invalidate(),
  })
}

/**
 * Clear it.
 *
 * A `404` is swallowed: it means there was nothing to clear, so the asset is
 * already in the state the user asked for. Re-throwing it would show an error
 * for an outcome that is not one — the correct response is the same refetch
 * as a `204`. Any other status re-throws.
 */
export function useDeleteAssetValue(kind: ValuationKind, id: string | null) {
  const invalidate = useInvalidateValue(kind)
  return useMutation({
    mutationFn: async () => {
      try {
        await valueApiFor(kind).remove(id as string)
      } catch (error) {
        if (statusOf(error) !== 404) throw error
      }
    },
    onSuccess: () => void invalidate(),
  })
}
